import { useEffect, useId, useRef } from 'react';
import { loadTinyMce, TINYMCE_BASE } from '../utils/tinymceLoader.js';

const BASE_INIT = {
  ...TINYMCE_BASE,
  menubar: false,
  plugins: 'lists link autolink code preview searchreplace wordcount',
  toolbar:
    'undo redo | blocks | bold italic underline | bullist numlist | link | removeformat | code preview',
};

/**
 * @param {{
 *   label?: string;
 *   value?: string;
 *   onChange: (value: string) => void;
 *   height?: number;
 *   init?: Record<string, unknown>;
 * }} props
 */
export function FormRichTextarea({ label, value, onChange, height = 280, init = {} }) {
  const reactId = useId().replace(/:/g, '');
  const editorId = `tinymce-${reactId}`;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    let cancelled = false;

    loadTinyMce()
      .then((tinymce) => {
        if (cancelled) {
          return;
        }

        tinymce.init({
          ...BASE_INIT,
          height,
          ...init,
          selector: `#${editorId}`,
          setup: (editor) => {
            editor.on('change input undo redo', () => {
              onChangeRef.current(editor.getContent());
            });
          },
          init_instance_callback: (editor) => {
            if (value) {
              editor.setContent(value);
            }
          },
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      window.tinymce?.remove(`#${editorId}`);
    };
  }, [editorId, height]);

  useEffect(() => {
    const editor = window.tinymce?.get(editorId);
    if (!editor) {
      return;
    }
    const next = value || '';
    if (editor.getContent() !== next) {
      editor.setContent(next);
    }
  }, [value, editorId]);

  return (
    <label className="field">
      {label ? <span>{label}</span> : null}
      <textarea id={editorId} defaultValue={value || ''} />
    </label>
  );
}
